import { Avatar, Card, CardContent, Typography, Grid, Box, IconButton } from '@mui/material';
import { Settings as EditIcon } from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { IInfluencer } from '../../../redux/buzzbridge/types';

interface ProfileHeaderProps {
    influencer: IInfluencer;
    onEdit?: () => void;
}

export function ProfileHeader({ influencer, onEdit }: ProfileHeaderProps) {
    const { themeStyle } = useThemeContext();

    // Calculate total reach across all platforms
    const calculateTotalReach = () => {
        let totalReach = 0;
        if (influencer.instagram?.followers) totalReach += influencer.instagram.followers;
        if (influencer.youtube?.followers) totalReach += influencer.youtube.followers;
        if (influencer.tiktok?.followers) totalReach += influencer.tiktok.followers;
        return totalReach.toLocaleString();
    };

    // Calculate average engagement rate
    const calculateAverageEngagement = () => {
        let totalEngagement = 0;
        let platformCount = 0;

        if (influencer.instagram?.engagementRate) {
            totalEngagement += influencer.instagram.engagementRate * 100;
            platformCount++;
        }
        if (influencer.youtube?.engagementRate) {
            totalEngagement += influencer.youtube.engagementRate * 100;
            platformCount++;
        }
        if (influencer.tiktok?.engagementRate) {
            totalEngagement += influencer.tiktok.engagementRate * 100;
            platformCount++;
        }

        return platformCount > 0 ? (totalEngagement / platformCount).toFixed(1) + '%' : '0%';
    };

    return (
        <Card
            elevation={1}
            sx={{ width: '100%', backgroundColor: theme[themeStyle].secondaryBg, position: 'relative' }}>
            {onEdit && (
                <IconButton
                    onClick={onEdit}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: theme[themeStyle].tag_white,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        zIndex: 1,
                    }}>
                    <EditIcon />
                </IconButton>
            )}
            <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={4} gap={2}>
                    <Avatar
                        src={influencer.profile_picture || influencer.instagram?.profile_picture || '/placeholder.svg'}
                        alt={influencer.name}
                        sx={{ width: 64, height: 64, fontSize: 24 }}>
                        {influencer.name?.charAt(0) || 'U'}
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: theme[themeStyle].tag_white }}>
                            {influencer.name || 'Unknown User'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme[themeStyle].tag_white }}>
                            {influencer.instagram?.username ||
                                influencer.youtube?.username ||
                                influencer.tiktok?.username ||
                                'No username'}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={2} textAlign="center">
                    <Grid item xs={4}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: theme[themeStyle].tag_white }}>
                            {calculateTotalReach()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme[themeStyle].tag_white }}>
                            Total Reach
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: theme[themeStyle].tag_white }}>
                            {calculateAverageEngagement()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme[themeStyle].tag_white }}>
                            Avg Engagement
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: theme[themeStyle].tag_white }}>
                            {influencer.ratings || 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme[themeStyle].tag_white }}>
                            Rating
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
